import { useCallback, useEffect, useMemo, useState } from "react";
import { listManufacturingPlants, type MfgMaterial, type MfgPlant } from "@/lib/manufacturingClient";
import { formatOutlookQty } from "@/lib/manufacturing/inventoryOutlookFormat";
import {
  closePackedAuditSession,
  getPackedAuditSession,
  openPackedAuditSession,
  postPackedAuditLine,
  reversePackedAuditLine,
  type PackedAuditLine,
  type PackedAuditSession,
} from "@/lib/manufacturing/packedAuditClient";
import { packedAuditDelta } from "@/lib/manufacturing/packedAuditMath";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import { WalkDictationPad } from "./WalkDictationPad";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "./manufacturingSurfaceClasses";

function usd(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function PackedAuditBody({ enabled, materials }: { enabled: boolean; materials: MfgMaterial[] }) {
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState("");
  const [session, setSession] = useState<PackedAuditSession | null>(null);
  const [lines, setLines] = useState<PackedAuditLine[]>([]);
  const [needWarning, setNeedWarning] = useState<string | null>(null);
  const [material, setMaterial] = useState<MfgMaterial | null>(null);
  const [packed, setPacked] = useState("");
  const [bay, setBay] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plantMaterials = useMemo(
    () => materials.filter((row) => row.plant_id === plantId),
    [materials, plantId],
  );

  const load = useCallback(async (id: string) => {
    const res = await getPackedAuditSession(id);
    if (!res.ok) {
      setError(res.error);
      setSession(null);
      setLines([]);
      return;
    }
    setSession(res.data.session);
    setLines(res.data.lines);
    setNeedWarning(res.data.need_warning);
    setError(null);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void listManufacturingPlants().then((res) => {
      if (!res.ok) return;
      const rows = Array.isArray(res.data.plants) ? res.data.plants : [];
      setPlants(rows);
      setPlantId((current) => current || rows[0]?.id || "");
    });
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !plantId) return;
    void load(plantId);
  }, [enabled, plantId, load]);

  const packedQty = Number(packed);
  const bayQty = Number(bay);
  const preview =
    material && Number.isFinite(packedQty) && packed.trim() !== "" && Number.isFinite(bayQty) && bay.trim() !== ""
      ? packedAuditDelta(material.on_hand_qty, packedQty, bayQty)
      : null;
  const hint = material ? session?.open_build_need?.[material.id] ?? 0 : 0;
  const writeOff = lines
    .filter((line) => !line.reversed_at && line.delta_qty < -0.0005)
    .reduce((sum, line) => sum + Math.abs(line.delta_qty) * line.unit_cost, 0);

  async function start() {
    if (!plantId) return;
    setBusy(true);
    const res = await openPackedAuditSession(plantId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSession(res.data.session);
    setLines(res.data.lines);
    setNeedWarning(res.data.need_warning);
    setError(null);
  }

  async function confirm() {
    if (!session || !material || !preview) return;
    setBusy(true);
    const res = await postPackedAuditLine({
      session_id: session.id,
      material_id: material.id,
      packed_qty: packedQty,
      bay_qty: bayQty,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setLines((current) => {
      const rest = current.filter((line) => line.material_id !== res.data.line.material_id);
      return [...rest, res.data.line];
    });
    setMaterial(null);
    setPacked("");
    setBay("");
    setError(null);
  }

  async function reverse(lineId: string) {
    setBusy(true);
    const res = await reversePackedAuditLine(lineId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setLines((current) => current.map((line) => (line.id === res.data.line.id ? res.data.line : line)));
  }

  async function closeWalk() {
    if (!session) return;
    setBusy(true);
    const res = await closePackedAuditSession(session.id);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSession(null);
    setLines([]);
  }

  return (
    <div className="mt-3 space-y-3">
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        This corrects today&apos;s books for the materials you look at. It does not close a plant inventory.
      </p>
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
        Plant
        <select className={inputClass} value={plantId} onChange={(e) => setPlantId(e.target.value)}>
          {plants.length === 0 ? <option value="">No plants</option> : null}
          {plants.map((plant) => (
            <option key={plant.id} value={plant.id}>
              {plant.plant_code ? `${plant.plant_code} · ${plant.name}` : plant.name}
            </option>
          ))}
        </select>
      </label>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {needWarning ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Open-build need could not be loaded ({needWarning}). Bay quantities are still yours to enter.
        </p>
      ) : null}
      {!session ? (
        <div className="space-y-2">
          <button type="button" className={mfgPrimaryBtnClass} disabled={!plantId || busy} onClick={() => void start()}>
            {busy ? "Starting…" : "Start packed audit"}
          </button>
          {busy ? (
            <p className="text-sm text-gray-600 dark:text-zinc-400">Opening this walk. It can take a moment.</p>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-700 dark:text-zinc-300">
              Write-off on this walk: <strong>{usd(writeOff)}</strong>
            </p>
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void closeWalk()}>
              Close walk
            </button>
          </div>
          <WalkDictationPad
            kind="packed"
            materials={plantMaterials}
            disabled={busy}
            onApply={({ material: next, primaryQty, secondaryQty }) => {
              setMaterial(next);
              setPacked(String(primaryQty));
              setBay(secondaryQty == null ? "" : String(secondaryQty));
            }}
          />
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
            {material ? (
              <p className="text-sm text-gray-900 dark:text-zinc-100">
                {material.code} · {material.name}. Books {formatOutlookQty(material.on_hand_qty)} {material.unit}.
                Open builds still need {formatOutlookQty(hint)}.
              </p>
            ) : (
              <MaterialSearchableSelect
                materials={plantMaterials}
                onSelect={(row) => {
                  setMaterial(row);
                  setPacked("");
                  setBay("");
                }}
              />
            )}
            {material ? (
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-sm text-gray-700 dark:text-zinc-300">
                  Packed
                  <input className={inputClass} inputMode="decimal" value={packed} onChange={(e) => setPacked(e.target.value)} />
                </label>
                <label className="text-sm text-gray-700 dark:text-zinc-300">
                  In the bays
                  <input className={inputClass} inputMode="decimal" value={bay} onChange={(e) => setBay(e.target.value)} />
                </label>
                <button type="button" className={ghostBtnClass} onClick={() => setMaterial(null)}>
                  Change material
                </button>
                <button type="button" className={mfgPrimaryBtnClass} disabled={!preview || busy} onClick={() => void confirm()}>
                  Confirm
                </button>
              </div>
            ) : null}
            {preview && material ? (
              <p className="text-sm text-gray-700 dark:text-zinc-300">
                Audited on hand {formatOutlookQty(preview.auditedOnHand)} {material.unit}. Delta{" "}
                {formatOutlookQty(preview.deltaQty)}
                {preview.kind === "write_off" ? " tentative write-off." : preview.kind === "found" ? " found stock." : " even."}
              </p>
            ) : null}
          </div>
          <ul className="space-y-2">
            {lines.map((line) => (
              <li key={line.id} className="rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-700">
                <p className="font-medium text-gray-900 dark:text-zinc-100">
                  {line.material_code} · {line.material_name}
                </p>
                <p className="text-gray-600 dark:text-zinc-400">
                  Books {formatOutlookQty(line.book_on_hand_qty)}, packed {formatOutlookQty(line.packed_qty)}, bays{" "}
                  {formatOutlookQty(line.bay_qty)}, hint {formatOutlookQty(line.open_build_need_qty)}, audited{" "}
                  {formatOutlookQty(line.audited_on_hand_qty)}, delta {formatOutlookQty(line.delta_qty)}.
                </p>
                {line.reversed_at ? (
                  <p className="text-gray-500">Reversed</p>
                ) : (
                  <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void reverse(line.id)}>
                    Reverse
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
